import { formatClockTime } from '@/utils/dateUtils'
import { escapeHtml } from '@/utils/htmlUtils'

import { prTarget, requestPill, type MyWorkPill } from './my-work.utils'
import type {
  ActionOffer,
  ActionRequest,
  FindingsStandard,
  MergePreviewPr,
  MergeBlockerAction,
  MergeGroup,
  MergeRequest,
  MergeResult,
  MyPr,
  MergeSettings,
  MyWorkCounts,
  SyncResult
} from './my-work.types'

/** What each blocker's action says before it is sent, for a fleet that lists none on the row. */
export const MERGE_QUEUE_OFFERS: Record<
  MergeBlockerAction,
  Omit<ActionOffer, 'id' | 'action' | 'available'>
> = {
  pr_announce: {
    label: 'Nudge reviewer',
    confirm:
      "Posts the PR's two-line heads-up in each reviewer's squad channel, as you. Channels that already " +
      'have this PR, and reviewers who approved, are skipped.'
  },
  pr_diagnose_build: {
    label: 'Diagnose build',
    confirm: "Reads the failed build's log and posts on the PR why it failed and what to do next."
  },
  pr_fix_all: {
    label: 'Fix findings',
    confirm:
      'Runs the fix loop on the PR now: every open finding is checked, the real ones fixed and the wrong ones disputed.'
  },
  pr_update_branch: {
    label: 'Update branch',
    confirm:
      "Merges the destination into the PR's branch on your own machine, as /agents:my-sweep does. A conflict is " +
      'resolved only when the tests stay green. The push makes Claude review the PR again.'
  },
  pr_rereview: {
    label: 'Review',
    confirm: "Runs Claude's standard review of the PR at its current commit."
  }
}

/** The one action each "Needs work" list offers. */
export const MERGE_GROUP_ACTIONS: Partial<Record<MergeGroup, MergeBlockerAction>> = {
  approval: 'pr_announce',
  build: 'pr_diagnose_build',
  findings: 'pr_fix_all',
  branch: 'pr_update_branch',
  review: 'pr_rereview'
}

export const MERGING_TEXT = 'Merging on your machine…'

/** The row's own offer for an action, or the stand-in above when the fleet sent none. */
export function offerFor(pr: MyPr, action: MergeBlockerAction): ActionOffer {
  const own = pr.actions?.find(offer => offer.action === action)
  return own ?? { id: action, action, available: true, ...MERGE_QUEUE_OFFERS[action] }
}

/** Ready by the fleet's rule when it sent one; otherwise by the page's older reading of the facts. */
export function isMergeReady(pr: MyPr, fallback: (pr: MyPr) => boolean): boolean {
  return pr.merge ? pr.merge.ready : fallback(pr)
}

/**
 * The buttons on one of my PRs: the action its list calls for, and Update branch on a ready PR
 * that is behind its destination. Every other offer stays in the ⋮ menu.
 */
export function mergeRowOffers(
  pr: MyPr,
  groupAction?: MergeBlockerAction
): { primary: ActionOffer[]; more: ActionOffer[] } {
  const wanted: MergeBlockerAction[] = []
  if (groupAction) wanted.push(groupAction)
  else if (pr.merge?.ready && (pr.behind ?? 0) > 0) wanted.push('pr_update_branch')
  const primary = wanted.map(action => offerFor(pr, action)).filter(offer => offer.available)
  const more = (pr.actions ?? []).filter(
    offer => !primary.some(shown => shown.action === offer.action)
  )
  return { primary, more }
}

export const DETAILS_LOADING_TEXT = 'Details still loading'

const ET_CLOCK = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })
/** Intl may put a narrow or no-break space before AM/PM; the fleet's words carry a plain one. */
const plainSpaces = (text: string): string => text.replace(/[\u202f\u00a0]/g, ' ')

/**
 * The fleet's words with its "11:48 AM ET" for `readyAt` (a PR with no PR pipeline, ready once its latest commit has
 * been quiet a while) in the viewer's own time, as ADX shows every other time. Unchanged without `readyAt`.
 */
export function localReadyTime(text: string, readyAt?: string): string {
  const local = formatClockTime(readyAt)
  if (!local) return text
  const et = plainSpaces(`${ET_CLOCK.format(new Date(readyAt!))} ET`)
  return plainSpaces(text).split(et).join(plainSpaces(local))
}

/**
 * What the PR still waits on, in one short line: "Waiting on: Claude's review of the latest commit ·
 * build", or "Details still loading" while Bitbucket has not sent its latest details. A fleet older
 * than `waitingFor` gets its blockers' own words.
 */
export function blockerSummary(pr: MyPr): string {
  const merge = pr.merge
  if (!merge) return ''
  if (!merge.waitingFor) return localReadyTime(merge.blockers.map(blocker => blocker.label).join(' · '), merge.readyAt)
  const waiting = merge.waitingFor.length ? localReadyTime(`Waiting on: ${merge.waitingFor.join(' · ')}`, merge.readyAt) : ''
  if (!merge.detailsLoading) return waiting
  return waiting ? `${DETAILS_LOADING_TEXT}. ${waiting}` : DETAILS_LOADING_TEXT
}

/** The whole ready checklist for the row's hover, one condition a line, each met (✓) or with what it waits on. */
export function checklistHoverHtml(pr: MyPr): string {
  return (pr.merge?.checklist ?? [])
    .map(check =>
      check.met
        ? `✓ ${escapeHtml(check.text)}`
        : `✗ ${escapeHtml(check.text)}: ${escapeHtml(localReadyTime(check.waiting.join('; '), pr.merge?.readyAt))}`
    )
    .join('<br>')
}

/** The two findings standards, in the Merge settings dialog's words. */
export const FINDINGS_STANDARD_OPTIONS: { value: FindingsStandard; label: string }[] = [
  {
    value: 'strict',
    label: 'No open findings: every finding fixed, resolved or declined with a reason'
  },
  { value: 'blockingOnly', label: 'No blocking findings: non-blocking ones may stay open' }
]

/** What counts as ready, in plain words, under the chosen findings standard. */
export function readyChecklist(standard: FindingsStandard): string[] {
  return [
    'Claude approves the latest commit with no blocking findings',
    standard === 'blockingOnly'
      ? 'No blocking findings open (non-blocking ones may stay)'
      : 'No open findings: each one fixed, resolved or declined with a reason',
    'The build passed',
    "Bitbucket's merge checks pass, including any approvals your branch requires",
    'No conflicts, and not held (nobody asked for changes)'
  ]
}

/** "develop, or main in fictional-api and fictional-web": where a merge from My work may land. */
export function mergeDestinationsText(mainRepos: string[]): string {
  if (!mainRepos.length) return 'develop'
  return `develop, or main in ${listText(mainRepos)}`
}

/** "a, b and c". */
export function listText(items: string[]): string {
  if (items.length < 2) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/** The preview, split the way the dialog shows it. Close means one condition is left. */
export interface MergePreviewGroups {
  mergeNow: MergePreviewPr[]
  readyForClick: MergePreviewPr[]
  close: MergePreviewPr[]
  others: number
}

export function mergePreviewGroups(prs: MergePreviewPr[]): MergePreviewGroups {
  const mergeNow = prs.filter(pr => pr.ready && pr.autoMerge)
  const readyForClick = prs.filter(pr => pr.ready && !pr.autoMerge)
  const close = prs.filter(pr => !pr.ready && !pr.pending && pr.unmet === 1)
  const others = prs.length - mergeNow.length - readyForClick.length - close.length
  return { mergeNow, readyForClick, close, others }
}

/** How old the fleet's read of the person's PRs is: "40s ago", "3 min ago", "2 h ago". */
export function previewAgeText(ageS: number): string {
  if (ageS < 60) return `${Math.max(0, Math.round(ageS))}s ago`
  if (ageS < 3600) return `${Math.round(ageS / 60)} min ago`
  return `${Math.round(ageS / 3600)} h ago`
}

export const UPDATING_TEXT = 'Updating the branch on your machine…'

type OwnRequest = Pick<MergeRequest, 'kind' | 'state' | 'result' | 'detail'>

/** The merge or Update branch the person asked for, as it stands now, or '' when there is none to report. */
export function mergeStatusText(request: OwnRequest | undefined): string {
  if (!request) return ''
  return request.kind === 'sync' ? syncStatusText(request) : mergeOutcomeText(request)
}

function mergeOutcomeText(request: OwnRequest): string {
  if (request.state !== 'done') return MERGING_TEXT
  switch (request.result) {
    case 'merged':
      return 'Merged.'
    case 'refused':
      return `Bitbucket refused the merge: ${request.detail ?? 'no reason given.'}`
    case 'gate-unmet':
      return `Not merged: ${request.detail ?? 'it was no longer ready.'}`
    case 'expired':
      return 'Your machine did not pick it up; start /runner and try again.'
    case 'withdrawn':
      return 'Withdrawn: Merge when ready was turned off before it merged.'
    case 'error':
      return `The merge failed: ${request.detail ?? 'no detail.'}`
    default:
      return ''
  }
}

function syncStatusText(request: OwnRequest): string {
  if (request.state !== 'done') return UPDATING_TEXT
  switch (request.result) {
    case 'updated':
      return 'Branch updated; Claude reviews it again.'
    case 'current':
      return 'Already up to date with its destination.'
    case 'conflict':
      return (
        request.detail ??
        'It conflicts with its destination; Claude is resolving it on your machine.'
      )
    case 'expired':
      return 'Your machine did not pick it up; start /runner and try again.'
    case 'refused':
    case 'error':
      return `The branch update failed: ${request.detail ?? 'no detail.'}`
    default:
      return ''
  }
}

const SYNC_FAILURES: ReadonlySet<string> = new Set(['refused', 'error', 'expired'])

/** A merge that did not merge, or an Update branch that did not update, shows as a failure. */
export function isMergeRefused(
  request: Pick<MergeRequest, 'kind' | 'result'> | undefined
): boolean {
  if (!request?.result) return false
  return request.kind === 'sync'
    ? SYNC_FAILURES.has(request.result)
    : request.result !== 'merged' && request.result !== 'withdrawn'
}

/** Recent actions knows a request's kind only by its action. */
function ownRequestOf(
  request: Pick<ActionRequest, 'action' | 'state' | 'result' | 'detail'>
): OwnRequest {
  const kind = request.action === 'pr_update_branch' ? 'sync' : 'merge'
  return { kind, state: request.state, result: request.result, detail: request.detail }
}

/** The Merge confirm: which PRs, into where, and that it is the person's own merge. */
/** What a merge confirm needs of a PR: My work's own rows, or a teammate's ready row on Needs attention. */
export type MergeablePr = Pick<MyPr, 'repo' | 'id' | 'title' | 'destination'> & Partial<Pick<MyPr, 'ticketKey'>>

/** `ownerName`: the PR is a teammate's, which the confirm says before the merge happens as the viewer. */
export function mergeConfirmMessage(prs: MergeablePr[], ownerName?: string): string {
  const lines = prs.map(
    pr =>
      `${escapeHtml(prTarget(pr))} · ${escapeHtml(pr.title)} → ${escapeHtml(pr.destination ?? '?')}`
  )
  const whose = ownerName
    ? `It is ${escapeHtml(ownerName)}'s PR; you merge it, so the merge is recorded as yours.<br><br>`
    : ''
  return (
    `${lines.join('<br>')}<br><br>` +
    whose +
    "Merges with your own Bitbucket credential on your machine, never the shared bot. Bitbucket's merge checks still apply, " +
    'and if it refuses, its reason shows here.'
  )
}

/** Merge when ready comes from the PR's repo being in "Merge automatically in", not a tick on the PR. */
export function isAutoRepo(pr: MyPr): boolean {
  const merge = pr.merge
  if (!merge) return false
  return merge.autoRepo ?? (merge.autoMergeSource === 'repo' || merge.autoMergeSource === 'all')
}

/** The switch's words: "automatic (adx)" while the repo merges on its own, and the PR's own override. */
export function autoMergeDescription(pr: MyPr): string {
  if (!isAutoRepo(pr)) return pr.merge?.confirmed?.testing ? 'Merge when ready, then Testing' : 'Merge when ready'
  if (pr.merge?.autoOverride === 'off') return `Not merged automatically (${pr.repo} is, this PR is off)`
  return `Merge when ready: automatic (${pr.repo})`
}

/**
 * What flipping the switch asks the fleet for. On a repo that merges automatically, turning it off
 * keeps this PR from merging on its own, and turning it back on drops that override (null) so the
 * PR follows its repo again. Elsewhere it ticks or unticks the PR.
 */
export function autoMergeChoice(pr: MyPr, on: boolean): boolean | null {
  if (isAutoRepo(pr)) return on ? null : false
  return on
}

/** What turning Merge when ready on will do, for its confirm dialog. */
export interface AutoMergeConfirmText {
  title: string
  lines: string[]
  testingLabel: string
}

/**
 * The confirm dialog's words, the same as the fleet's confirm page for the PR's own Merge when ready link
 * (tattle-claude-fleet src/mywork-merge.ts autoMergeConfirmWords): the PR, the branch it merges into, the ready rule,
 * and the Testing step, which happens only when the ticket is assigned to the person and has no other open PR.
 */
export function autoMergeConfirmText(pr: MergeablePr, ownerName?: string): AutoMergeConfirmText {
  const target = prTarget(pr).replace('#', ' #')
  const into = pr.destination ?? 'its destination branch'
  const teammate = ownerName
    ? [
        `It is ${ownerName}'s PR. You turn this on for it, so your runner merges it as you; ${ownerName} can still merge it ` +
          'first, and their own Merge when ready is unchanged.'
      ]
    : []
  return {
    title: ownerName ? `Merge ${ownerName}'s PR ${target} when ready` : `Merge ${target} when ready`,
    lines: [
      `${pr.title} → ${into}`,
      `Once ${target} is ready, your own runner (/runner) merges it into ${into} with your own Bitbucket credential, ` +
        "within about five minutes. Bitbucket's merge checks still apply.",
      "Ready means Claude's verdict on the latest commit approves with no blocking findings and every Claude finding " +
        'is settled, the build passed, the approvals are in, and no other thread, task or change request is open. ' +
        'Nothing merges before then: it waits while another merge into the same branch is running, and you can turn ' +
        'it off until it merges.',
      ...teammate
    ],
    testingLabel:
      `After the merge, move ${pr.ticketKey ?? 'its ticket'} to Testing with the one-line Merged comment: ` +
      'only if it is assigned to you and has no other open PR.' +
      (ownerName ? ` It is ${ownerName}'s PR, so this starts off: their ticket is usually assigned to them.` : '')
  }
}

/** The person's settings with every field present: an older fleet sends only `autoMergeAll`. */
export function mergeSettingsView(settings: MergeSettings): Required<MergeSettings> {
  return {
    ...settings,
    autoRepos: settings.autoRepos ?? (settings.autoMergeAll ? [...settings.repos] : []),
    autoReposDefault: settings.autoReposDefault ?? false,
    allowOpenNonBlocking: settings.allowOpenNonBlocking ?? false,
    findingsStandard: settings.findingsStandard ?? 'strict',
    mainRepos: settings.mainRepos ?? []
  }
}

/** The side nav badge: PRs ready to merge plus decisions waiting; unknown counts as none. */
export function badgeCount(counts: MyWorkCounts | null | undefined): number {
  if (!counts) return 0
  return (counts.readyToMerge ?? 0) + (counts.decisions ?? 0)
}

export function badgeText(count: number): string {
  return count > 99 ? '99+' : String(count)
}

const MERGE_RESULT_PILLS: Record<MergeResult, MyWorkPill> = {
  merged: { label: 'Merged', color: 'green' },
  refused: { label: 'Not merged', color: 'red' },
  'gate-unmet': { label: 'Not merged', color: 'red' },
  error: { label: 'Failed', color: 'red' },
  expired: { label: 'Not picked up', color: 'yellow' },
  withdrawn: { label: 'Withdrawn', color: 'gray' }
}

const SYNC_RESULT_PILLS: Record<SyncResult, MyWorkPill> = {
  updated: { label: 'Updated', color: 'green' },
  current: { label: 'Up to date', color: 'green' },
  conflict: { label: 'Resolving conflict', color: 'blue' },
  refused: { label: 'Failed', color: 'red' },
  error: { label: 'Failed', color: 'red' },
  expired: { label: 'Not picked up', color: 'yellow' }
}

/**
 * A finished merge or Update branch says how it ended, since "Done" would hide a refusal; any other
 * request, or a result this page does not know, shows its state.
 */
export function requestStatePill(
  request: Pick<ActionRequest, 'action' | 'state' | 'result'>
): MyWorkPill {
  const pills: Partial<Record<string, MyWorkPill>> =
    request.action === 'pr_update_branch' ? SYNC_RESULT_PILLS : MERGE_RESULT_PILLS
  const pill = request.state === 'done' && request.result ? pills[request.result] : undefined
  return pill ?? requestPill(request.state)
}

/**
 * The line under a merge or Update branch in Recent actions: Bitbucket's reason when it refused, and
 * the like. A merge says how to undo it, since a merge is final in Bitbucket.
 */
export function requestResultText(
  request: Pick<ActionRequest, 'action' | 'state' | 'result' | 'detail'>
): string {
  const own = ownRequestOf(request)
  if (!request.result) return ''
  if (own.kind === 'merge' && request.result === 'merged') return mergedUndoText(request.detail)
  return mergeStatusText(own)
}

/** The fleet sends the merge commit's hash as a merged request's detail. */
function mergedUndoText(commit: string | undefined): string {
  const hash = commit && /^[0-9a-f]{7,40}$/i.test(commit) ? ` ${commit.slice(0, 7)}` : ''
  return `To undo, open a PR that reverts merge commit${hash}.`
}
